import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

// Tab generico guidato dalla configurazione del modulo: nessuna business logic nel frontend.
export default function ModuleTab({ companyId, periodId, config, sites, canWrite, onChanged, registersSummary }) {
  const [records, setRecords] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    if (!companyId || !periodId) return;
    try {
      const { data } = await api.get(`/companies/${companyId}/periods/${periodId}/environmental/${config.key}`);
      setRecords(data);
    } catch (err) {
      toast.error("Errore caricamento", { description: formatApiError(err) });
    }
  }, [companyId, periodId, config.key]);

  useEffect(() => { load(); }, [load, registersSummary]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(
        `/companies/${companyId}/periods/${periodId}/environmental/${config.key}`, { data: form }
      );
      toast.success("Record aggiunto");
      setDialogOpen(false);
      setForm({});
      load();
      if (onChanged && data.summary) onChanged(data.summary);
    } catch (err) {
      toast.error("Errore salvataggio", { description: formatApiError(err) });
    }
  };

  const remove = async (rec) => {
    try {
      const { data } = await api.delete(
        `/companies/${companyId}/periods/${periodId}/environmental/${config.key}/${rec.id}`
      );
      toast.success("Record eliminato");
      load();
      if (onChanged && data.summary) onChanged(data.summary);
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const renderField = (f) => {
    const val = form[f.key];
    if (f.type === "select") {
      return (
        <Select value={val != null ? String(val) : ""} onValueChange={(v) => setField(f.key, f.numeric ? Number(v) : v)}>
          <SelectTrigger data-testid={`field-${config.key}-${f.key}`}><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent>
            {f.options.map((o) => (
              <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    if (f.type === "site") {
      return (
        <Select value={val || ""} onValueChange={(v) => setField(f.key, v)}>
          <SelectTrigger data-testid={`field-${config.key}-${f.key}`}><SelectValue placeholder="Sede legale / non specificata" /></SelectTrigger>
          <SelectContent>
            {sites.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.nome} {s.citta ? `— ${s.citta}` : ""}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    if (f.type === "textarea") {
      return <Textarea rows={2} value={val || ""} onChange={(e) => setField(f.key, e.target.value)} data-testid={`field-${config.key}-${f.key}`} />;
    }
    if (f.type === "boolean") {
      return (
        <Select value={val === true ? "true" : val === false ? "false" : ""} onValueChange={(v) => setField(f.key, v === "true")}>
          <SelectTrigger data-testid={`field-${config.key}-${f.key}`}><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="true">Sì</SelectItem>
            <SelectItem value="false">No</SelectItem>
          </SelectContent>
        </Select>
      );
    }
    return (
      <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} step="any"
        value={val ?? ""} onChange={(e) => setField(f.key, f.type === "number" ? (e.target.value === "" ? null : e.target.valueAsNumber) : e.target.value)}
        data-testid={`field-${config.key}-${f.key}`} />
    );
  };

  return (
    <div className="space-y-4" data-testid={`tab-content-${config.key}`}>
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600 max-w-2xl leading-relaxed">{config.description}</p>
        {canWrite && (
          <Button className="bg-green-700 hover:bg-green-800" onClick={() => setDialogOpen(true)} data-testid={`add-${config.key}`}>
            <Plus className="w-4 h-4 mr-1" aria-hidden="true" /> {config.addLabel}
          </Button>
        )}
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <Table data-testid={`table-${config.key}`}>
            <TableHeader>
              <TableRow>
                {config.columns.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}
                {canWrite && <TableHead className="text-right">Azioni</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.length === 0 && (
                <TableRow>
                  <TableCell colSpan={config.columns.length + 1} className="text-center text-slate-500 py-8" data-testid={`empty-${config.key}`}>
                    Nessun dato inserito per questo periodo.
                  </TableCell>
                </TableRow>
              )}
              {records.map((r) => (
                <TableRow key={r.id} data-testid={`record-${config.key}-${r.id.slice(0, 8)}`}>
                  {config.columns.map((c) => (
                    <TableCell key={c.key}>{c.render ? c.render(r) : (r[c.key] ?? "—")}</TableCell>
                  ))}
                  {canWrite && (
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" className="text-red-700" onClick={() => remove(r)} data-testid={`delete-${config.key}-${r.id.slice(0, 8)}`}>
                        <Trash2 className="w-4 h-4" aria-hidden="true" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`dialog-${config.key}`}>
          <DialogHeader>
            <DialogTitle>{config.addLabel}</DialogTitle>
            <DialogDescription>{config.dialogHint || "Inserisci il dato operativo: il sistema gestisce conversioni e calcoli."}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {config.fields.map((f) => (
              <div key={f.key} className={`space-y-1.5 ${f.full ? "md:col-span-2" : ""}`}>
                <Label>{f.label}{f.required ? " *" : ""}</Label>
                {renderField(f)}
                {f.hint && <p className="text-xs text-slate-500">{f.hint}</p>}
              </div>
            ))}
            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid={`cancel-${config.key}`}>Annulla</Button>
              <Button type="submit" className="bg-green-700 hover:bg-green-800" data-testid={`submit-${config.key}`}>Salva e calcola</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function StatusChip({ value }) {
  if (!value) return null;
  return <Badge variant="outline" className="bg-slate-50 text-slate-600 text-[11px]">{value}</Badge>;
}
